/**
 * Il manuale, dentro la pagina (GI-281).
 *
 * **Prima il proprio capitolo, aperto; gli altri chiusi, sotto.** Il manuale
 * si legge per ruolo: chi carica non ha bisogno del sigillo, e chi appone il
 * sigillo non ha bisogno del caricamento — ma ha bisogno di sapere chi
 * riceve il suo lavoro, e per questo gli altri capitoli restano a portata.
 *
 * **Una copia sola.** I capitoli stanno in `./manuale/`, letti come testo al
 * momento della costruzione: `docs/manuale-operatore/README.md` vi rimanda,
 * non li ripete. Il manuale e' in italiano, la lingua di chi lavora sulle
 * linee: la scheda inglese lo dice, invece di mostrare una pagina vuota.
 */
import type { Whoami } from '../auth'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { Markdown } from './markdown'
import chiCarica from './manuale/01-chi-carica.md?raw'
import operatore from './manuale/02-operatore-di-qualita.md?raw'
import supervisoreQualita from './manuale/03-supervisore-qualita.md?raw'
import supervisoreConsegna from './manuale/04-supervisore-consegna.md?raw'
import responsabile from './manuale/05-responsabile.md?raw'

interface Chapter {
  id: string
  /** I mestieri per cui questo capitolo e' «il proprio». */
  roles: string[]
  source: string
}

export const CHAPTERS: Chapter[] = [
  { id: 'chi-carica', roles: ['uploader'], source: chiCarica },
  { id: 'operatore', roles: ['qa_operator'], source: operatore },
  { id: 'supervisore-qualita', roles: ['supervisor'], source: supervisoreQualita },
  { id: 'supervisore-consegna', roles: ['supervisor'], source: supervisoreConsegna },
  { id: 'responsabile', roles: ['job_manager'], source: responsabile },
]

/** Il titolo del capitolo e' la sua prima riga `# …`. */
export function titleOf(source: string): string {
  const first = /^#\s+(.*)$/m.exec(source)
  return first?.[1] ?? ''
}

export function Manuale({ who }: { who: Whoami }): JSX.Element {
  const t = useT()
  const mine = CHAPTERS.filter((one) => one.roles.includes(who.role))
  const others = CHAPTERS.filter((one) => !one.roles.includes(who.role))
  const note: MessageKey = 'aiuto.manualLanguage'
  return (
    <main className="aiuto-manuale">
      <p className="t-read">{t('aiuto.manualIntro')}</p>
      <p className="tenue">{t(note)}</p>
      {mine.map((one) => (
        <article key={one.id} id={one.id} className="aiuto-capitolo">
          <Markdown source={one.source} />
        </article>
      ))}
      {others.length > 0 && (
        <section aria-labelledby="aiuto-altri">
          <h2 id="aiuto-altri">{mine.length === 0 ? t('aiuto.chapters') : t('aiuto.otherChapters')}</h2>
          {others.map((one) => (
            <details key={one.id} id={one.id} className="aiuto-capitolo">
              <summary>{titleOf(one.source)}</summary>
              <Markdown source={one.source} />
            </details>
          ))}
        </section>
      )}
    </main>
  )
}
